import {Component, Inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ProleisSuppliersState} from '../../store/store.index';
import {Store} from '@ngrx/store';
import {combineLatest, exhaustMap, forkJoin, Observable, of, take, tap} from 'rxjs';
import {IncidentsActions, IncidentsSelectors} from '../../store/incidents/incidents.index';
import {APP_GUIDS, AppGuids} from '../../../app.guids';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {CreateCommentComponent} from '../create-comment/create.comment.component';
import {FileResponse} from '../../../models/file.response';
import {saveAs} from 'file-saver';
import {map} from 'rxjs/operators';
import {SafePipe} from '../../../safe.pipe';
import {SearchPositionDialogComponent} from '../../orders/search-position/search.position.dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {
  CreatePlannedDeliveryComponent
} from '../../orders/overlays/create-planned-delivery/create.planned.delivery.component';
import {DeliveriesActions, DeliveriesSelectors} from '../../store/deliveries/deliveries.index';
import {PositionSelectors} from '../../store/positions/position.index';
import {DocumentService, ImageService, Incident, ProleisObject, UserService} from "@proleis/proleis-rest-client";
import {Image} from "@proleis/proleis-web-gallery";

@Component({
  selector: 'ps-incidents-details',
  templateUrl: 'incident.details.component.html',
  styleUrls: ['incident.details.component.scss']
})
export class IncidentDetailsComponent implements OnInit {

  user: ProleisObject;

  viewModel$: Observable<IncidentDetailsViewModel> = combineLatest({
    pos: this.store.select(PositionSelectors.selectPosition),
    incident: this.store.select(IncidentsSelectors.selectIncident),
    comments: this.store.select(IncidentsSelectors.selectComments),
    documents: this.store.select(IncidentsSelectors.selectDocuments),
    canCreateDelivery: this.canCreateDelivery(),
    previewImages: this.store.select(IncidentsSelectors.selectDocuments).pipe(
      exhaustMap(documents => {
        return this.loadPreviewImages(documents);
      })
    ),
    deliveries: this.store.select(DeliveriesSelectors.SELECT_PLANNED_DELIVERIES).pipe(
      tap(deliveries => console.log('unfiltered:', deliveries)),
      map(deliveries => {
        return deliveries.filter(del => Number(del.LINK_VORFALL_ID) === Number(this.activatedRoute.snapshot.params.incidentId));
      }),
      tap(deliveries => console.log('filtered:', deliveries))
    ),
    icons: this.store.select(DeliveriesSelectors.SELECT_DELIVERY_STATUS_ICONS)
  });

  constructor(private activatedRoute: ActivatedRoute, private store: Store<ProleisSuppliersState>,
              @Inject(APP_GUIDS) private appGuids: AppGuids, private userService: UserService, private bottomSheet: MatBottomSheet,
              private ds: DocumentService, private is: ImageService, private safePipe: SafePipe, private matDialog: MatDialog) {
  }

  ngOnInit() {
    this.userService.getCurrentUser().subscribe(user => {
      this.user = user;
    });

    this.loadIncident();
    this.loadComments();
    this.loadDocuments();
    this.loadIncidents();
    this.loadDeliveries();
  }

  loadIncident() {
    this.store.dispatch(IncidentsActions.SELECT_INCIDENT({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId,
      incidentId: this.activatedRoute.snapshot.params.incidentId,
      aliasFields: 'SNR=LINK_ID.PARENTANDMYSELF[TYP_ID.GUID=' + this.appGuids.GUID_TEILE_TYP + '].NAME;' +
        'ORDERNR=LINK_ID.PARENTANDMYSELF[TYP_ID.GUID=' + this.appGuids.GUID_TEILE_TYP + '].CHILD[TYP_ID.GUID=' +
        this.appGuids.GUID_PSETYP_TB + '].PBO_BESTELL_POS_ID.PARENT.NAME;CATEGORYNAME=HAUPTKATEGORIE_ID.NAME;ERRORNAME=KATEGORIE_ID.NAME' +
        ';PRIONAME=PRIO.TEXT;STATUSNAME=PROBLEM_STATUS_ID.NAME'
    }));
  }

  loadDeliveries() {
    this.store.dispatch(DeliveriesActions.LOAD_PLANNED_DELIVERIES({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId
    }));
  }

  loadComments() {
    this.store.dispatch(IncidentsActions.LOAD_INCIDENT_COMMENTS({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId,
      incidentId: this.activatedRoute.snapshot.params.incidentId,
      aliasFields: 'USERNAME=BENUTZER_ID.TEXT'
    }));
  }

  loadDocuments() {
    this.store.dispatch(IncidentsActions.LOAD_INCIDENT_DOCUMENTS({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId,
      incidentId: this.activatedRoute.snapshot.params.incidentId,
    }));
  }

  loadIncidents() {
    this.store.dispatch(DeliveriesActions.LOAD_PLANNED_DELIVERIES({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId
    }));
  }

  canCreateDelivery(): Observable<boolean> {
    this.store.dispatch(DeliveriesActions.VALIDATE_PBO_SUBTYPES({
      pboId: this.activatedRoute.snapshot.params.id,
      pseId: this.activatedRoute.snapshot.params.posId
    }));

    const validatedPbos = this.store.select(DeliveriesSelectors.SELECT_VALIDATED_PBOS);
    return validatedPbos.pipe(
      map(result => {
        if (result) {
          return result.some(e => e.guid === this.appGuids.GUID_TYPE_RECEIVE_QS_PARTS);
        } else {
          return false;
        }
      })
    );
  }

  loadPreviewImages(documents: ProleisObject[]): Observable<Image[]> {
    const images = documents.filter(
      doc => doc.TEXT.toLowerCase().endsWith('jpg') || doc.TEXT.toLowerCase().endsWith('jpeg') || doc.TEXT.toLowerCase().endsWith('png')
    );

    if (images.length <= 0) {
      return of([]);
    }

    const requests = images.map(img => {
      return this.ds.getDocumentFile(img.OBJECT_ID);
    });

    return forkJoin(requests).pipe(
      map(result => {
        return result.map(response => {
          const fileResponse: FileResponse = FileResponse.parseResponse(response);
          return {
            alt: fileResponse.fileName,
            path: this.safePipe.transform(URL.createObjectURL(fileResponse.content))
          };
        });
      })
    );
  }

  getAuthorClass(comment: ProleisObject) {
    if (comment.BENUTZER_ID === this.user?.OBJECT_ID) {
      return 'comment-self';
    } else {
      return 'comment-other';
    }
  }

  openCreateCommentSheet(incident: ProleisObject) {
    this.bottomSheet.open(CreateCommentComponent, {data: incident.OBJECT_ID}).afterDismissed().subscribe(result => {
      if (result.comment) {
        this.loadComments();
      }
    });
  }

  downloadDocument(doc: ProleisObject) {
    this.ds.getDocumentFile(doc.OBJECT_ID).subscribe(response => {
      const fileResponse: FileResponse = FileResponse.parseResponse(response);
      saveAs(fileResponse.content, fileResponse.fileName);
    });
  }

  acceptIncident() {
    this.store.dispatch(IncidentsActions.ACCEPT_INCIDENT({
      orderId: this.activatedRoute.snapshot.params.id,
      posId: this.activatedRoute.snapshot.params.posId,
      incidentId: this.activatedRoute.snapshot.params.incidentId,
      aliasFields: 'SNR=LINK_ID.PARENTANDMYSELF[TYP_ID.GUID=' + this.appGuids.GUID_TEILE_TYP + '].NAME;' +
        'ORDERNR=LINK_ID.PARENTANDMYSELF[TYP_ID.GUID=' + this.appGuids.GUID_TEILE_TYP + '].CHILD[TYP_ID.GUID=' +
        this.appGuids.GUID_PSETYP_TB + '].PBO_BESTELL_POS_ID.PARENT.NAME;CATEGORYNAME=HAUPTKATEGORIE_ID.NAME;ERRORNAME=KATEGORIE_ID.NAME' +
        ';PRIONAME=PRIO.TEXT;STATUSNAME=PROBLEM_STATUS_ID.NAME'
    }));
  }

  openSearchDialog() {
    this.matDialog.open(SearchPositionDialogComponent);
  }

  openCreatePlannedDeliveryBottomsheet(incident: Incident) {
    this.viewModel$.pipe(
      take(1)
    ).subscribe(viewModel => {
      if (viewModel.canCreateDelivery) {
        this.bottomSheet.open(CreatePlannedDeliveryComponent, {
          data: {
            pboId: this.activatedRoute.snapshot.params.id,
            pseId: this.activatedRoute.snapshot.params.posId,
            incident,
            fromIncident: true
          }
        }).afterDismissed().subscribe(result => {
          console.log(result);
        });
      }
    });
  }
}

export interface IncidentDetailsViewModel {
  incident: ProleisObject;
  documents: ProleisObject[];
  comments: ProleisObject[];
  previewImages: Image[];
  canCreateDelivery: boolean;
  deliveries: ProleisObject[];
  pos: ProleisObject;
  icons: Map<string, string>;
}
